import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// MD3 chip system per DESIGN.md.
// - Base: inline-flex pill, MD3 container fill, container text role.
// - Status variants only ever appear at chip size (here).
// - `*-light` / `*-dark` keys are kept for caller back-compat and route to the
//   same MD3 container/on-container pair.
const badgeVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full transition-colors duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  {
    variants: {
      size: {
        default: "h-6 px-2.5 type-label-m",
        sm: "h-5 px-2 type-label-s",
      },
      variant: {
        default: "bg-surface-variant text-on-surface-variant",
        outline: "border border-outline-variant text-on-surface-variant bg-transparent",

        // Primary (ink-violet) tonal container
        primary: "bg-primary-container text-on-primary-container",
        "primary-light": "bg-primary-container text-on-primary-container",
        "primary-dark": "bg-primary-container text-on-primary-container",

        // Secondary (ink-blue) tonal container
        secondary: "bg-secondary-container text-on-secondary-container",
        "secondary-light": "bg-secondary-container text-on-secondary-container",
        "secondary-dark": "bg-secondary-container text-on-secondary-container",

        // Tertiary (warm amber) tonal container
        tertiary: "bg-tertiary-container text-on-tertiary-container",

        // Status tonal containers
        info: "bg-info-container text-on-info-container",
        "info-light": "bg-info-container text-on-info-container",
        "info-dark": "bg-info-container text-on-info-container",

        success: "bg-success-container text-on-success-container",
        "success-light": "bg-success-container text-on-success-container",
        "success-dark": "bg-success-container text-on-success-container",

        warning: "bg-warning-container text-on-warning-container",
        "warning-light": "bg-warning-container text-on-warning-container",
        "warning-dark": "bg-warning-container text-on-warning-container",

        error: "bg-error-container text-on-error-container",
        "error-light": "bg-error-container text-on-error-container",
        "error-dark": "bg-error-container text-on-error-container",

        // Outline variants — outline-variant border, on-container text
        "outline-primary": "border border-outline-variant text-on-primary-container bg-transparent",
        "outline-secondary": "border border-outline-variant text-on-secondary-container bg-transparent",
        "outline-info": "border border-outline-variant text-on-info-container bg-transparent",
        "outline-success": "border border-outline-variant text-on-success-container bg-transparent",
        "outline-warning": "border border-outline-variant text-on-warning-container bg-transparent",
        "outline-error": "border border-outline-variant text-on-error-container bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
